/**
 * Downscale a photo before it goes anywhere near the network.
 *
 * A modern phone camera produces 3-6 MB per shot. Nobody needs that for a
 * thumbnail of a gym floor, and on a bad connection it is the difference
 * between a check-in that syncs and one that sits in the outbox.
 */
const MAX_EDGE = 1280;
const QUALITY = 0.82;

export async function compressImage(file: File): Promise<Blob> {
  // createImageBitmap handles EXIF orientation for us on every browser we care
  // about; if it is missing or the format is exotic, send the original.
  if (typeof createImageBitmap !== "function") return file;

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    return file;
  }

  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext("2d");
  if (!context) {
    bitmap.close();
    return file;
  }

  context.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", QUALITY),
  );

  // If compression somehow made it bigger, keep the original.
  return blob && blob.size < file.size ? blob : file;
}
